
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Review } from '@/types/review';
import { Card, CardContent } from '@/components/ui/card';
import { RatingFilter } from '@/components/admin/reviews/RatingFilter';
import { ReviewsTable } from '@/components/admin/reviews/ReviewsTable';
import { ReviewsPagination } from '@/components/admin/reviews/ReviewsPagination';
import { ReviewStats } from '@/components/admin/reviews/ReviewStats';

// Extended type to include total count field from paginated query
interface PaginatedReview extends Review {
  total_count: number;
}

export function ReviewsList() {
  const [ratingFilter, setRatingFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10; // Number of reviews per page

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-reviews', ratingFilter, currentPage, pageSize],
    queryFn: async () => {
      const filterRating = ratingFilter && ratingFilter !== 'all' ? parseInt(ratingFilter, 10) : null;
      
      const { data, error } = await supabase.rpc(
        'get_admin_reviews_with_course_info',
        { 
          rating_filter: filterRating, 
          page_number: currentPage,
          page_size: pageSize
        }
      );
      
      if (error) throw error;
      
      // Log the review data for debugging
      console.log('Reviews data from Supabase:', data);
      
      return data as PaginatedReview[];
    },
  });

  const handleFilterChange = (value: string) => {
    setRatingFilter(value === 'all' ? null : value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const totalPages = data && data.length > 0 ? Math.ceil(data[0].total_count / pageSize) : 0;
  const totalReviews = data && data.length > 0 ? data[0].total_count : 0;

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-destructive">Error loading reviews: {(error as Error).message}</p>
        </CardContent>
      </Card>
    );
  }

  const reviews = data || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <RatingFilter value={ratingFilter} onChange={handleFilterChange} />
        <ReviewStats totalReviews={totalReviews} ratingFilter={ratingFilter} />
      </div>

      <ReviewsTable reviews={reviews} isLoading={isLoading} />
      
      <ReviewsPagination 
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
}
